import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/tos")({
  component: TOS
});

function TOS() {
  return (
    <div className="bg-background text-tx-primary scroll-primary flex h-screen w-screen flex-col overflow-auto p-8 antialiased">
      <h1 className="text-tx-primary mb-4 text-3xl font-bold">Last updated: September 20, 2024</h1>
      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">Welcome to Moe Capital LLC's Terms of Service!</h2>
      <p className="mb-6">
        These Terms of Service ("Terms") are a binding legal agreement between you ("you" or "your") and Moe Capital
        LLC, a Virginia limited liability company ("Moe Capital," "we," "us," or "our") governing your access to and use
        of our large language model (LLM) chat services, including our websites, applications, and any other products or
        services offered by Moe Capital (collectively, the "Services").
      </p>
      <p className="mb-6">
        Please read these Terms carefully before accessing or using the Services. By accessing or using the Services,
        you agree to be bound by these Terms. If you disagree with any part of the Terms, then you may not access or use
        the Services.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">1. Definitions</h2>
      <p className="mb-6">
        "Account" means an account required to access and use certain features of the Services.
        <br />
        "Content" means any and all information and content that is submitted to, or uploaded, transmitted, or otherwise
        made available through, the Services, such as text, data, files, images, videos, audio, and any other materials.
        <br />
        "User Content" means Content that you submit to, or upload, transmit, or otherwise make available through, the
        Services.
        <br />
        "Moe Capital Content" means all Content that is not User Content.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">2. Eligibility</h2>
      <p className="mb-6">
        You must be at least 18 years old to use the Services. By accessing or using the Services, you represent and
        warrant that you are at least 18 years old and have the legal capacity to enter into these Terms. If you are
        accessing or using the Services on behalf of a company or other legal entity, you represent and warrant that you
        have the authority to bind that entity to these Terms.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">3. Account Registration and Security</h2>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">3.1 Account Registration</h3>
      <p className="mb-6">
        You may be required to create an Account to access and use certain features of the Services. If you create an
        Account, you agree to provide accurate, current, and complete information during the registration process and to
        keep your Account information updated. You are solely responsible for maintaining the confidentiality of your
        Account login information, including your username and password, and for all activity that occurs under your
        Account. You agree to notify us immediately of any unauthorized access to or use of your Account.
      </p>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">3.2 Account Security</h3>
      <p className="mb-6">
        You are responsible for taking reasonable steps to protect your Account from unauthorized access, use, or
        disclosure. You agree not to share your Account login information with any third party. We are not responsible
        for any loss or damage arising from your failure to comply with this Section 3.2.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">4. User Content</h2>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">4.1 User Content License</h3>
      <p className="mb-6">
        You retain all of your ownership rights in and to your User Content. However, by submitting, uploading,
        transmitting, or otherwise making available User Content through the Services, you hereby grant to Moe Capital a
        non-exclusive, royalty-free, worldwide, perpetual, irrevocable, sublicensable, and transferable license to use,
        reproduce, modify, adapt, publish, translate, create derivative works from, distribute, perform, and display
        your User Content in connection with the operation and provision of the Services.
      </p>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">4.2 User Content Representations and Warranties</h3>
      <p className="mb-6">
        You represent and warrant that: (a) you own all rights in and to your User Content or have obtained all
        necessary rights and permissions to grant the licenses granted in these Terms; (b) your User Content does not
        and will not infringe, misappropriate, or violate any third party's intellectual property rights, privacy
        rights, or other legal or moral rights; (c) your User Content does not and will not violate these Terms or any
        applicable law or regulation; and (d) your User Content is not and will not be false, inaccurate, misleading,
        defamatory, libelous, obscene, pornographic, indecent, lewd, suggestive, harassing, threatening, harmful,
        invasive of privacy or publicity rights, abusive, inflammatory, fraudulent, or otherwise objectionable.
      </p>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">4.3 User Content Monitoring</h3>
      <p className="mb-6">
        We reserve the right, but have no obligation, to monitor, review, edit, remove, or disable access to any User
        Content for any reason, at any time, without notice to you. We are not responsible for any User Content, and we
        do not endorse any opinions or views expressed in any User Content.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">5. Prohibited Conduct</h2>
      <p className="mb-6">
        You agree not to:
        <ul className="list-inside list-disc">
          <li>Use the Services for any illegal or unauthorized purpose.</li>
          <li>Violate any applicable local, state, national, or international law or regulation.</li>
          <li>Infringe upon the intellectual property rights of others.</li>
          <li>
            Impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a person
            or entity.
          </li>
          <li>Interfere with or disrupt the Services or servers or networks connected to the Services.</li>
          <li>
            Attempt to gain unauthorized access to the Services, other Accounts, computer systems, or networks connected
            to the Services.
          </li>
          <li>Use any robot, spider, scraper, or other automated means to access the Services for any purpose.</li>
          <li>
            Introduce any viruses, Trojan horses, worms, logic bombs, or other material that is malicious or
            technologically harmful.
          </li>
          <li>
            Attempt to decipher, decompile, disassemble, or reverse engineer any of the software comprising or in any
            way making up a part of the Services.
          </li>
          <li>
            Use the Services in any manner that could damage, disable, overburden, or impair the Services or interfere
            with any other party's use of the Services.
          </li>
          <li>
            Use the Services to transmit any unsolicited or unauthorized advertising, promotional materials, "junk
            mail," "spam," "chain letters," "pyramid schemes," or any other form of solicitation.
          </li>
          <li>
            Use the Services to collect or harvest any personally identifiable information, including Account names,
            from the Services.
          </li>
          <li>Use the Services to stalk, harass, threaten, or harm another individual.</li>
          <li>Use the Services to promote bigotry, hatred, or violence against any group or individual.</li>
          <li>
            Use the Services to engage in any activity that we deem, in our sole discretion, to be inappropriate or
            harmful.
          </li>
        </ul>
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">6. Third-Party Content and Services</h2>
      <p className="mb-6">
        The Services may contain links to third-party websites, applications, or other services that are not owned or
        controlled by Moe Capital. We have no control over, and assume no responsibility for, the content, privacy
        policies, or practices of any third-party websites, applications, or services. You acknowledge and agree that
        Moe Capital shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged
        to be caused by or in connection with the use of or reliance on any such content, goods, or services available
        on or through any such websites or services.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">7. Copyright Complaints</h2>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">7.1</h3>
      <p className="mb-6">
        We respect the intellectual property rights of others and expect users of the Services to do the same. We will
        respond to notices of alleged copyright infringement that comply with applicable law and are properly provided
        to us.
      </p>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">7.2</h3>
      <p className="mb-6">
        If you believe that your copyrighted work has been copied in a way that constitutes copyright infringement,
        please provide our copyright agent with the following information:
        <ul className="list-inside list-disc">
          <li>
            A physical or electronic signature of the copyright owner or a person authorized to act on their behalf;
          </li>
          <li>Identification of the copyrighted work claimed to have been infringed;</li>
          <li>
            Identification of the material that is claimed to be infringing or to be the subject of infringing activity
            and that is to be removed or access to which is to be disabled, and information reasonably sufficient to
            permit us to locate the material;
          </li>
          <li>Your contact information, including your address, telephone number, and an email address;</li>
          <li>
            A statement by you that you have a good faith belief that use of the material in the manner complained of is
            not authorized by the copyright owner, its agent, or the law;
          </li>
          <li>
            A statement that the information in the notification is accurate, and, under penalty of perjury, that you
            are authorized to act on behalf of the copyright owner.
          </li>
        </ul>
      </p>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">7.3</h3>
      <p className="mb-6">
        We reserve the right (but not the obligation) to remove content alleged to be infringing without prior notice,
        at our sole discretion. In appropriate circumstances, we will also terminate a user's account if the user is
        determined to be a repeat infringer.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">8. Termination</h2>
      <p className="mb-6">
        We may terminate your access to and use of the Services, or any portion thereof, immediately and at any time,
        with or without cause and without notice, for any reason or no reason, including but not limited to your
        violation of these Terms or any other agreement between you and Moe Capital. Upon termination, your right to use
        the Services will immediately cease.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">9. Disclaimers</h2>
      <p className="mb-6">
        THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
        BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
        NON-INFRINGEMENT. MOE CAPITAL DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, THAT
        DEFECTS WILL BE CORRECTED, OR THAT THE SERVICES ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. YOU ACKNOWLEDGE
        THAT YOU ARE USING THE SERVICES AT YOUR OWN RISK.
      </p>
      <p className="mb-6">
        MOE CAPITAL MAKES NO REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE OPERATION OF THE
        SERVICES, OR THE INFORMATION, CONTENT, MATERIALS, OR PRODUCTS INCLUDED ON THE SERVICES. TO THE FULLEST EXTENT
        PERMISSIBLE BY APPLICABLE LAW, MOE CAPITAL DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT
        LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">10. Limitation of Liability</h2>
      <p className="mb-6">
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL MOE CAPITAL, ITS AFFILIATES, LICENSORS,
        SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR ANY DAMAGES OF ANY KIND, UNDER ANY
        LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE SERVICES, ANY WEBSITES
        LINKED TO IT, ANY CONTENT ON THE SERVICES OR SUCH OTHER WEBSITES, INCLUDING ANY DIRECT, INDIRECT, INCIDENTAL,
        SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, PERSONAL INJURY, PAIN AND SUFFERING,
        EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS
        OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE,
        EVEN IF FORESEEABLE.
      </p>
      <p className="mb-6">
        THIS LIMITATION SHALL APPLY EVEN IF MOE CAPITAL HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>
      <p className="mb-6">
        BECAUSE SOME STATES/JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF LIABILITY FOR CONSEQUENTIAL OR
        INCIDENTAL DAMAGES, THE ABOVE LIMITATION MAY NOT APPLY TO YOU. IN SUCH STATES/JURISDICTIONS, MOE CAPITAL'S
        LIABILITY IS LIMITED TO THE GREATEST EXTENT PERMITTED BY LAW.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">11. Indemnification</h2>
      <p className="mb-6">
        You agree to indemnify, defend, and hold harmless Moe Capital, its affiliates, licensors, service providers,
        employees, agents, officers, and directors from and against any and all claims, liabilities, damages, losses,
        costs, and expenses (including attorneys' fees) arising out of or in connection with: (a) your use of the
        Services; (b) your violation of these Terms; (c) your violation of any third-party right, including without
        limitation any copyright, property, or privacy right; or (d) any claim that your User Content caused damage to a
        third party.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">12. Dispute Resolution</h2>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">12.1 Governing Law and Jurisdiction</h3>
      <p className="mb-6">
        These Terms and any action related thereto will be governed by and construed in accordance with the laws of the
        State of Virginia, without regard to its conflict of laws provisions.
      </p>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">12.2 Arbitration</h3>
      <p className="mb-6">
        Any dispute arising out of or relating to these Terms or your access to or use of the Services, including any
        dispute as to the validity, enforceability, or scope of this Section 12.2, shall be resolved by binding
        arbitration before a single arbitrator administered by the American Arbitration Association ("AAA") in
        accordance with its Commercial Arbitration Rules and the Supplementary Procedures for Consumer Related Disputes
        then in effect for the AAA, except as provided herein. The arbitrator shall be bound by these Terms and shall
        issue a reasoned award in writing. The arbitrator shall not have the power to award any damages not expressly
        authorized by these Terms. Judgment on the award rendered by the arbitrator may be entered in any court having
        competent jurisdiction.
      </p>
      <h3 className="text-tx-tertiary mb-2 text-xl font-semibold">12.3 Class Action Waiver</h3>
      <p className="mb-6">
        YOU AND MOE CAPITAL AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY
        AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING. Further, unless both
        you and Moe Capital agree otherwise, the arbitrator may not consolidate more than one person's claims, and may
        not otherwise preside over any form of a representative or class proceeding. If this specific provision is found
        to be unenforceable, then the entirety of this Dispute Resolution Section shall be null and [void.](void..md)
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">13. Severability</h2>
      <p className="mb-6">
        If any provision of these Terms is found to be unenforceable or invalid, in whole or in part, such provision
        shall be modified to the minimum extent necessary to make it valid and enforceable. If such modification is not
        possible, the provision shall be severed from these Terms. In either case, the validity and enforceability of
        the remaining provisions of these Terms shall not be affected. The parties agree to replace any invalid or
        unenforceable term or provision with a term or provision that is valid and enforceable and that comes closest to
        expressing the intention of the invalid or unenforceable term or provision.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">14. Entire Agreement</h2>
      <p className="mb-6">
        These Terms constitute the entire agreement between you and Moe Capital relating to your access to and use of
        the Services and supersede all prior or contemporaneous written or oral agreements between you and Moe Capital
        with respect to such subject matter. These Terms may not be amended or modified except by a written agreement
        signed by an authorized representative of Moe Capital.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">15. Contact Us</h2>
      <p className="mb-6">
        If you have any questions about our Terms of Services, Privacy Policy, or Legal Notices, please contact us at{" "}
        <a href="mailto:hey@anime.gf" className="text-blue-500 hover:underline">
          hey@anime.gf
        </a>
        .
      </p>
    </div>
  );
}
